import { Injectable } from '@angular/core';
import { ICompany, ICompanyCountry, ICountry, IDescribedObject, IFaq, ILov, INamedObject } from '../../../type_definitions/datatypes';
import { HttpDataService, IHttpDataCache } from '../http/http-data.service';


export interface ITicketGuideButton {
  title:string;
  code:string;
  description:string;
  workflowType:string;
}

export interface ISimpleTicket extends IDescribedObject {
  status:string;
  category:ITicketCategory;
  accessible:boolean;
}

export interface ITicket {
  category?:ITicketCategory;
  comments?:string;
  country?:ICountry;
  createdDate?:number;
  description?:string;
  editLink?:string;
  id?:number;
  orderId?:number;
  company?:ICompany;
  owner?:string;
  status?:string;
  urgency?:ILov;
  webshop?:INamedObject;
  originalRequester?:number;
  companyCountry?:ICompanyCountry;
}

export interface ITicketCategory {
  categoryType?:string;
  description?:string;
  workflowType?:string;
  comments?:string;
}

export interface ITicketCreationContext {
  email:string;
  firstName:string;
  lastName:string;
  company:ICompany;
  country:ICountry;
  id?:number;
  subject:string;
  description:string;
  poNumber:number;
  serialNumber:string;
  category:ITicketCategory;
}


export const TICKET_WORKFLOW_TYPE_ORDER:string = 'TODO_PO';
export const TICKET_WORKFLOW_TYPE_WEBSHOP:string = 'TODO_PR';
export const TICKET_WORKFLOW_TYPE_TRAINING:string = 'TODO_TRAIN';
export const TICKET_CATEGORY_TYPE_B2B:string = 'B2B';
const TICKET_GUIDE_FAQ_CANCEL_ID = 'TICKET_GUIDE_FAQ';

@Injectable()
export class TicketDataService {
  private cache:IHttpDataCache<IFaq[]>;

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  public getTicketGuideButtons(workflowTypes: string): Promise<ITicketGuideButton[]> {
    return this.httpDataService.doGET(
      'rest/ticket/guidebuttons',
      { workflowTypes },
      undefined,
      'Error loading ticket guide buttons'
    );
  }

  public getTicketFaqs(workflowType:string, ticketCategoryType:string):Promise<IFaq[]> {
    return <Promise<IFaq[]>>this.cache.doGET(
      'rest/ticket/faqs',
      {
        workflowType,
        ticketCategoryType
      },
      undefined,
      'Error loading faqs',
      TICKET_GUIDE_FAQ_CANCEL_ID
    );
  }

  public submitTicket(model:ITicket, profileId?:number):Promise<ITicket> {
    return this.httpDataService.doPOST(
      'rest/tickets',
      { profileId },
      model,
      'Submitting a ticket...',
      'Failed to submit the ticket'
    );
  }

  public getCategories(ticketGuideButtonCode:string, workflowType?:string):Promise<ITicketCategory[]> {
    return <Promise<ITicketCategory[]>>this.cache.doGET(
      'rest/ticket/categories',
      {
        ticketGuideButtonCode,
        workflowType
      },
      undefined,
      'Error loading categories'
    );
  }

  public getTicketContext(contextId:string):Promise<ITicketCreationContext> {
    return this.httpDataService.doGET(
      `rest/ticket/context`,
      { contextId },
      undefined,
      'Error loading ticket context'
    );
  }

  public createExternalTicket(contextId:string, data:ITicketCreationContext):Promise<ITicketCreationContext> {
    return this.httpDataService.doPOST(
      `rest/ticket/external`,
      { contextId },
      data,
      undefined,
      'Error creating external ticket'
    );
  }

  public clearCache():void {
    this.cache.clear();
  }
}